import {
  SearchCategory,
  SearchParentCategory,
  Category,
} from '@backrs_ui/grow-ui/src/pages/Search/search.types';

type HandleMenuItemClickProps = Readonly<{
  clickedKey: SearchCategory;
  event: React.MouseEvent<HTMLElement>;
  menuItems: SearchParentCategory['categories'];
  selectedItemsInThisDropdown: SearchCategory[];
  selectedItemsFromOtherDropdowns: SearchCategory[];
  onSelect: (newItems: SearchCategory[]) => void;
  setAnchorEl: (anchorEl: HTMLElement | null) => void;
  setSelectedParentKey: (selectedParentKey: SearchCategory | null) => void;
}>;

export const handleMenuItemClick = ({
  clickedKey,
  event,
  menuItems,
  selectedItemsInThisDropdown,
  selectedItemsFromOtherDropdowns,
  onSelect,
  setAnchorEl,
  setSelectedParentKey,
}: HandleMenuItemClickProps) => {
  const isParent = menuItems.find((item) => item.key === clickedKey)?.isParent;

  if (isParent) {
    setAnchorEl(event.currentTarget);
    setSelectedParentKey(clickedKey);

    return;
  }

  const isAlreadySelected = selectedItemsInThisDropdown.includes(clickedKey);
  const newSelectedValues: SearchCategory[] = isAlreadySelected
    ? selectedItemsInThisDropdown.filter((value) => value !== clickedKey)
    : [...selectedItemsInThisDropdown, clickedKey];

  const clickedItem = menuItems.find((item) => item.key === clickedKey);

  // If adding an "All" option, only select it (while preserving selections from other dropdowns)
  if (!isAlreadySelected && clickedItem?.clearsOtherFilters) {
    onSelect([...selectedItemsFromOtherDropdowns, clickedKey]);

    return;
  }

  // If a non-"All" item is being added and an "All" option is already selected,
  // remove the "All" option from the current dropdown's selection.
  const updatedSelectedValues = !clickedItem?.clearsOtherFilters
    ? newSelectedValues.filter((key) => {
        const menuItem = menuItems.find((item) => item.key === key);

        return !menuItem?.clearsOtherFilters;
      })
    : newSelectedValues;

  // Combine with the selections from other dropdowns
  onSelect([...selectedItemsFromOtherDropdowns, ...updatedSelectedValues]);
};

type HandleParentCheckboxClickProps = Readonly<{
  item: Category;
  menuItems: SearchParentCategory['categories'];
  selectedItemsInThisDropdown: SearchCategory[];
  selectedItemsFromOtherDropdowns: SearchCategory[];
  onSelect: (newItems: SearchCategory[]) => void;
}>;

// Parent keys expand to their child keys, except an "All" parent, which is itself a filter.
export const handleParentCheckboxClick = ({
  item,
  menuItems,
  selectedItemsInThisDropdown,
  selectedItemsFromOtherDropdowns,
  onSelect,
}: HandleParentCheckboxClickProps) => {
  if (item.clearsOtherFilters) {
    const isSelected = selectedItemsInThisDropdown.includes(item.key);
    const ownKeys = [item.key, ...(item.subCategories || []).map((subItem) => subItem.key)];

    // An "All" parent clears only its own group, matching the sub menu route
    const keptSelections = selectedItemsInThisDropdown.filter((key) => !ownKeys.includes(key));

    onSelect(
      isSelected
        ? [...selectedItemsFromOtherDropdowns, ...keptSelections]
        : [...selectedItemsFromOtherDropdowns, ...keptSelections, item.key],
    );

    return;
  }

  const subKeys = (item.subCategories || []).map((subItem) => subItem.key);
  const keptSelections = selectedItemsInThisDropdown.filter((key) => !subKeys.includes(key));

  if (isParentFullySelected(item, selectedItemsInThisDropdown)) {
    onSelect([...selectedItemsFromOtherDropdowns, ...keptSelections]);

    return;
  }

  // Selecting a parent supersedes an "All" option in the same dropdown
  const withoutClearAll = keptSelections.filter((key) => {
    const menuItem = menuItems.find((entry) => entry.key === key);

    return !menuItem?.clearsOtherFilters;
  });

  onSelect([...selectedItemsFromOtherDropdowns, ...withoutClearAll, ...subKeys]);
};

export const isParentFullySelected = (item: Category, selectedItems: SearchCategory[]) => {
  if (item.clearsOtherFilters) {
    return selectedItems.includes(item.key);
  }

  const subKeys = (item.subCategories || []).map((subItem) => subItem.key);

  return subKeys.length > 0 && subKeys.every((key) => selectedItems.includes(key));
};

type HandleSubMenuClickProps = Readonly<{
  subItem: Category;
  selectedItems: SearchCategory[];
  onSelect: (newItems: SearchCategory[]) => void;
  menuItems: SearchParentCategory['categories'];
  firstLayerMenuItems: SearchParentCategory['categories'];
  selectedItemsInThisDropdown: SearchCategory[];
  selectedItemsFromOtherDropdowns: SearchCategory[];
}>;

export const handleSubMenuClick = ({
  subItem,
  selectedItems,
  onSelect,
  menuItems,
  firstLayerMenuItems,
  selectedItemsInThisDropdown,
  selectedItemsFromOtherDropdowns,
}: HandleSubMenuClickProps) => {
  const isAlreadySelected = selectedItemsInThisDropdown.includes(subItem.key);

  if (isAlreadySelected) {
    onSelect([
      ...selectedItemsFromOtherDropdowns,
      ...selectedItemsInThisDropdown.filter((key) => key !== subItem.key),
    ]);

    return;
  }

  const foundMenuItem = menuItems.find((item) => item.key === subItem.key);

  // handle clearing others in the sub menu items when a sub menu item Clears Others is picked
  if (foundMenuItem?.clearsOtherFilters) {
    const filteredFirstLayerMenuItemsAlreadySelected: SearchCategory[] = firstLayerMenuItems
      .map((item) => (item.key && selectedItems.includes(item.key) ? item.key : undefined))
      .filter((item) => item !== undefined);

    onSelect([
      ...selectedItemsFromOtherDropdowns,
      ...filteredFirstLayerMenuItemsAlreadySelected,
      subItem.key,
    ]);

    return;
  }

  // Get the first layer/up one layer menu items that clear others, this would be an All option
  const firstLayerMenuItemsThatClearOthers = firstLayerMenuItems.filter(
    (item) => item.clearsOtherFilters,
  );

  // Remove the firstLayerMenuItemsThatClearOthers from selectedItems
  const newSelection = selectedItems.filter(
    (item) =>
      !firstLayerMenuItemsThatClearOthers.map((firstLayer) => firstLayer.key).includes(item),
  );

  onSelect([...newSelection, subItem.key]);
};

export const filterSlug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-');

export const optionSlug = (key: SearchCategory) => key.toLowerCase().replace(/_/g, '-');
